<template>
  <base-dialog
    v-model="localValue"
    title="Billing Details"
    width="500px"
    :show-footer="true"
    :show-cancel="true"
    :show-confirm="true"
    cancel-label="Close"
    confirm-label="Download Invoice"
    confirm-button-class="base-dialog__primary-btn"
    :loading="loadingInvoice"
    :disabled="isInvoiceInactive"
    @close="$emit('close')"
    @cancel="$emit('close')"
    @confirm="downloadInvoice"
  >
    <div v-if="billing" class="billing-payments__details-content">
      <!-- Reference ID -->
      <div class="billing-payments__details-item">
        <div class="billing-payments__details-label">Reference ID</div>
        <div class="billing-payments__details-value billing-payments__details-reference">
          {{ billing.reference }}
        </div>
      </div>

      <!-- Price -->
      <div class="billing-payments__details-item">
        <div class="billing-payments__details-label">Amount</div>
        <div class="billing-payments__details-value billing-payments__details-price">
          {{ formatCurrency(billing.amount, billing.currency) }}
        </div>
      </div>

      <!-- Status -->
      <div class="billing-payments__details-item">
        <div class="billing-payments__details-label">Status</div>
        <div class="billing-payments__details-value">
          <span class="billing-payments__status-badge" :class="`billing-payments__status-badge--${billing.status}`">
            {{ capitalizeFirstLetter(billing.status) }}
          </span>
        </div>
      </div>

      <!-- Invoice Period -->
      <div class="billing-payments__details-item">
        <div class="billing-payments__details-label">Invoice Period</div>
        <div class="billing-payments__details-value">
          {{ formatDateRange(billing.period_start, billing.period_end) }}
        </div>
      </div>

      <div class="billing-payments__details-divider"></div>

      <!-- Name -->
      <div class="billing-payments__details-item">
        <div class="billing-payments__details-label">Billing Name</div>
        <div class="billing-payments__details-value">{{ billing.name }}</div>
      </div>

      <!-- Email -->
      <div class="billing-payments__details-item">
        <div class="billing-payments__details-label">Billing Email</div>
        <div class="billing-payments__details-value">{{ billing.email }}</div>
      </div>

      <!-- Phone -->
      <div class="billing-payments__details-item">
        <div class="billing-payments__details-label">Phone Number</div>
        <div class="billing-payments__details-value">{{ billing.phone || 'N/A' }}</div>
      </div>
    </div>
  </base-dialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog.vue';
import { BillingsApi } from '@/api/modules/billings';
import BillingFormat from '@/utils/mixins/billing-format';

export default {
  name: 'BillingDetailsDialog',
  components: {
    BaseDialog
  },
  mixins: [BillingFormat],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    billing: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loadingInvoice: false
    };
  },
  computed: {
    localValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    isInvoiceInactive() {
      return this.billing?.status === 'inactive';
    }
  },
  methods: {
    async downloadInvoice() {
      if (!this.billing || !this.billing.reference) {
        this.$q.notify({
          color: 'negative',
          message: 'Unable to download invoice: Missing subscription ID',
          position: 'top',
          timeout: 2000
        });
        return;
      }

      if (this.isInvoiceInactive) {
        this.$q.notify({
          color: 'warning',
          message: 'Cannot download invoice for inactive subscriptions',
          position: 'top',
          timeout: 2000
        });
        return;
      }

      this.loadingInvoice = true;
      try {
        const response = await BillingsApi.getInvoice(this.billing.reference);

        if (response?.data?.invoice_url) {
          window.open(response.data.invoice_url, '_blank');
        } else {
          throw new Error('Invoice URL not found');
        }
      } catch (error) {
        console.error('Error fetching invoice:', error);
        this.$q.notify({
          color: 'negative',
          message: 'Failed to download invoice',
          position: 'top',
          timeout: 2000
        });
      } finally {
        this.loadingInvoice = false;
      }
    }
  }
};
</script>

<style lang="scss">
@import './billing-payments-styles.scss';
</style>
